import React, { useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import dayjs from 'dayjs';
import { ListTable } from '../../../components/molecules/ListTable';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { leadStatusMeta, platformMeta } from './leadsMeta';
import { ConvertLeadPanel } from './ConvertLeadPanel';
import { MarketingLead } from '../../../services/marketingLeads';

/**
 * The leads table, shared by Lead Management and the Meta Ads screen so both
 * stay identical to the rest of the admin's list screens (Customers,
 * Technicians, …) rather than drifting into their own styling.
 */

type HeaderCell = [string, number];

const HEADER_CELLS: HeaderCell[] = [
  ['Name', 180],
  ['Source', 150],
  ['Campaign', 200],
  ['Received On', 210],
  ['ROAS', 110],
  ['Status', 150],
  ['Action', 110],
];

interface LeadsListTableProps {
  leads: MarketingLead[];
  isLoading?: boolean;
  noDataText?: string;
  /** Called after a lead is converted (or found already converted) so the caller can refetch its list. */
  onConverted?: () => void;
}

export const LeadsListTable: React.FC<LeadsListTableProps> = ({
  leads,
  isLoading = false,
  noDataText = 'No leads found for the selected filters yet.',
  onConverted,
}) => {
  const [selected, setSelected] = useState<MarketingLead | null>(null);

  const renderCell = (lead: MarketingLead, columnIndex: number) => {
    switch (columnIndex) {
      case 0:
        return (
          <Typography sx={{ fontSize: 13.5, color: '#e2e8f0', fontWeight: 600 }}>
            {lead.name || '—'}
          </Typography>
        );
      case 1: {
        const platform = platformMeta[lead.platform] || platformMeta.OTHER;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: platform.color }} />
            <Typography sx={{ fontSize: 13, color: '#e2e8f0' }}>{platform.label}</Typography>
          </Box>
        );
      }
      case 2:
        return (
          <Typography sx={{ fontSize: 13, color: dashboardTokens.textSecondary }}>
            {lead.campaignName || 'Uncategorized'}
          </Typography>
        );
      case 3:
        return (
          <Typography sx={{ fontSize: 13, color: dashboardTokens.textSecondary }}>
            {dayjs(lead.receivedOn).format('MMM D, YYYY, h:mm A')}
          </Typography>
        );
      case 4:
        return lead.roas != null ? (
          <Typography sx={{ fontSize: 13.5, color: '#22c55e', fontWeight: 600 }}>
            {lead.roas}x
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted }}>—</Typography>
        );
      case 5: {
        const status = leadStatusMeta[lead.status];
        return (
          <Chip
            label={status.label}
            size="small"
            sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, height: 24 }}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <ListTable<MarketingLead>
        headerCells={HEADER_CELLS}
        rows={leads}
        isLoading={isLoading}
        noDataText={noDataText}
        renderCell={renderCell}
        actions={[
          {
            icon: <VisibilityOutlinedIcon fontSize="small" />,
            onClick: (lead) => setSelected(lead),
          },
        ]}
      />

      <ConvertLeadPanel
        lead={selected}
        onClose={() => setSelected(null)}
        onConverted={onConverted}
      />
    </>
  );
};
